import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/Context';
import { fetchSubjectsByExam, getExamBySlug } from '../../services/exams.util';
import { checkExamPurchase, createExamPurchaseOrder, purchaseExam } from '../../services/purchase.util';
import { initializeRazorpayPayment } from '../../services/razorpay.util';
import { useMessageBox } from '../../components/others/MessageBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGraduationCap,
  faLock,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';

const RENEWAL_MESSAGE = 'Your subscription has ended. Please renew it to continue accessing this content.';

const ExamPage = () => {
  const { examCode } = useParams();
  const navigate = useNavigate();
  const { loggedIn, loggedInUser } = useAuthContext();
  const { showMessage } = useMessageBox();
  
  // Exam data state management
  const [examData, setExamData] = useState(null);
  const [examLoading, setExamLoading] = useState(true);
  const [examError, setExamError] = useState(null);
  
  // Subjects state management
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  const [subjectsError, setSubjectsError] = useState(null);
  
  // Purchase state management
  const [examPurchaseStatus, setExamPurchaseStatus] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Authentication protection - redirect to login if not authenticated
  useEffect(() => {
    if (!loggedIn) {
      navigate('/login', { replace: true });
      return;
    }
  }, [loggedIn, navigate]);

  // Fetch exam data when component mounts or examCode changes
  useEffect(() => {
    // Only load exam data if user is logged in
    if (!loggedIn) return;
    
    const loadExamData = async () => {
      try {
        setExamLoading(true);
        setExamError(null);
        
        const data = await getExamBySlug(examCode);
        setExamData(data);
      } catch (error) {
        console.error('Error loading exam data:', error);
        setExamError(error.message || 'Failed to load exam data. Please try again.');
      } finally {
        setExamLoading(false);
      }
    };

    loadExamData();
  }, [examCode, loggedIn]);

  // Fetch subjects when component mounts or examCode changes
  useEffect(() => {
    // Only load subjects if user is logged in
    if (!loggedIn) return;
    
    const loadSubjects = async () => {
      try {
        setSubjectsLoading(true);
        setSubjectsError(null);
        
        const data = await fetchSubjectsByExam(examCode);
        setSubjects(data || []);
      } catch (error) {
        console.error('Error loading subjects:', error);
        setSubjectsError(error.message || 'Failed to load subjects. Please try again.');
      } finally {
        setSubjectsLoading(false);
      }
    };

    loadSubjects();
  }, [examCode, loggedIn]);

  const refreshExamPurchaseStatus = useCallback(async () => {
    if (!loggedIn || !examData?._id) return;

    try {
      const response = await checkExamPurchase(examData._id);
      setExamPurchaseStatus(response.data);
    } catch (error) {
      console.error('Error checking exam purchase:', error);
      setExamPurchaseStatus({ hasPurchased: false });
    }
  }, [loggedIn, examData]);

  // Check exam purchase status
  useEffect(() => {
    refreshExamPurchaseStatus();
  }, [refreshExamPurchaseStatus]);

  const handlePaymentSuccess = useCallback(async (response) => {
    if (!examData?._id) return;

    try {
      showMessage('Verifying payment...', 'info');

      const purchaseResponse = await purchaseExam(examData._id, {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        purchaseType: 'full',
      });

      if (purchaseResponse.success) {
        showMessage('Payment successful! Exam purchased successfully.', 'success');
        await refreshExamPurchaseStatus();
      } else {
        throw new Error(purchaseResponse.message || 'Purchase verification failed');
      }
    } catch (error) {
      console.error('Purchase verification error:', error);
      showMessage(error.message || 'Payment verification failed', 'error');
    } finally {
      setProcessingPayment(false);
    }
  }, [examData, refreshExamPurchaseStatus, showMessage]);

  // Handle Razorpay payment messages from React Native WebView
  useEffect(() => {
    const handleMessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'RAZORPAY_SUCCESS') {
          await handlePaymentSuccess(data.data);
        } else if (data.type === 'RAZORPAY_ERROR') {
          const errorMessage = data.error?.description || data.error?.message || 'Payment could not be processed';
          showMessage(`Payment failed: ${errorMessage}. Please try again or contact support if the issue persists.`, 'error');
          setProcessingPayment(false);
        }
      } catch (e) {
        // Ignore non-JSON messages.
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handlePaymentSuccess, showMessage]);

  // Handle purchase button click
  const handlePurchaseClick = async (exam) => {
    if (!loggedInUser) {
      showMessage('Please login to purchase exams', 'error');
      return;
    }

    if (!exam?._id) {
      showMessage('Exam details are not available. Please try again.', 'error');
      return;
    }

    if (processingPayment) {
      showMessage('Payment is already being processed', 'warning');
      return;
    }

    try {
      setProcessingPayment(true);
      const orderResponse = await createExamPurchaseOrder(exam._id, {
        purchaseType: 'full',
      });

      if (!orderResponse.success) {
        throw new Error(
          orderResponse.message || 'Failed to create payment order'
        );
      }

      const { order, razorpayKeyId, callbackUrl } = orderResponse.data;
      showMessage('Creating payment order...', 'info');
      const options = {
        key: razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: 'gvkEDU',
        description: `Purchase ${exam.title} - Full Exam`,
        order_id: order.id,
        callback_url: callbackUrl,
        redirect: true,
        prefill: {
          contact: loggedInUser.mobileNumber || '',
          email: loggedInUser.email || '',
        },
        notes: {
          id: exam._id,
          examTitle: exam.title,
          purchaseType: 'full',
        },
        theme: {
          color: '#000000',
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
            showMessage('Payment cancelled', 'warning');
          },
        },
      };

      await initializeRazorpayPayment(options, (error) => {
        throw error;
      });
    } catch (error) {
      console.error('Purchase error:', error);
      showMessage(error.message || 'Failed to initiate payment', 'error');
      setProcessingPayment(false);
    }
  };

  // Loading state
  if (examLoading || subjectsLoading) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-white">Loading exam data...</h5>
                <p className="text-light">Please wait while we fetch the exam information.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (examError || subjectsError) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-danger mb-3">
                  <FontAwesomeIcon icon={faGraduationCap} size="3x" />
                </div>
                <h5 className="text-white">Error Loading Data</h5>
                <p className="text-light mb-4">{examError || subjectsError}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No exam data state
  if (!examData) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-secondary mb-3">
                  <FontAwesomeIcon icon={faGraduationCap} size="3x" />
                </div>
                <h5 className="text-white">Exam Not Found</h5>
                <p className="text-light">The requested exam could not be found. Please check the URL and try again.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No subjects state
  if (subjects.length === 0) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4">
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <div className="text-secondary mb-3">
                  <FontAwesomeIcon icon={faGraduationCap} size="3x" />
                </div>
                <h5 className="text-white">No Subjects Available</h5>
                <p className="text-light">There are currently no subjects available for this exam. Please check back later.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  // Get subject pricing for current exam (for display purposes only)
  const getSubjectPricing = (subject) => {
    if (!subject.examPricing || !examData) return null;
    const pricing = subject.examPricing.find(p => 
      (p.exam?._id || p.exam) === examData._id
    );
    return pricing || null;
  };

  // Check if subject is accessible (only through exam purchase or if free)
  const isSubjectAccessible = (subject) => {
    // If exam is purchased, all subjects are accessible
    if (examPurchaseStatus?.hasPurchased) {
      return true;
    }
    // Subject-level free access
    if (subject?.isFree) {
      return true;
    }
    // Check if subject is free
    const pricing = getSubjectPricing(subject);
    return pricing?.isFree || false;
  };
  const isSubscriptionExpired = examPurchaseStatus?.isExpired;

  return (
    <div className="main-content">
      <style>{`
        /* Main content background */
        .main-content {
          background: #000000;
          min-height: 100vh;
          position: relative;
        }
        
        .main-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgZmlsbC1ydWxlPSJub256ZXJvIj4KICAgICAgICAgICAgPGNpcmNsZSBjeD0iNyIgY3k9IjciIHI9IjciLz4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==');
          opacity: 0.4;
          pointer-events: none;
        }
        
        .container-fluid {
          position: relative;
          z-index: 1;
        }

        /* Subject Card Styling - Matching WhyCourse.jsx */
        .subject-card-modern {
          position: relative;
          border-radius: 28px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateY(0);
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.85) 0%, 
            rgba(20, 20, 20, 0.8) 50%, 
            rgba(0, 0, 0, 0.85) 100%
          );
          backdrop-filter: blur(25px) saturate(180%);
          -webkit-backdrop-filter: blur(25px) saturate(180%);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 
                      0 0 0 1px rgba(255, 255, 255, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.15);
          will-change: transform, box-shadow;
          animation: cardFloat 6s ease-in-out infinite;
        }

        .subject-card-modern:nth-child(2n) {
          animation-delay: 2s;
        }

        .subject-card-modern:nth-child(3n) {
          animation-delay: 4s;
        }

        .subject-card-modern::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }

        .subject-card-modern::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.1) 0%, 
            transparent 30%, 
            rgba(255, 255, 255, 0.05) 100%
          );
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }

        .subject-card-modern:hover {
          transform: translateY(-20px) scale(1.03);
          box-shadow: 0 50px 100px rgba(0, 0, 0, 0.6), 
                      0 0 80px rgba(255, 255, 255, 0.15), 
                      0 0 0 2px rgba(255, 255, 255, 0.3),
                      inset 0 1px 0 rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.4);
        }

        .subject-card-modern:hover::after {
          opacity: 1;
        }

        .subject-card-modern:hover .subject-shine-effect {
          left: 120% !important;
          opacity: 1;
        }

        /* Enhanced Content Styling */
        .subject-content {
          position: relative;
          z-index: 2;
        }

        /* Enhanced Icon Section */
        .subject-icon {
          position: relative;
          text-align: left;
        }

        .subject-icon::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 25px;
          transform: translateY(-50%);
          width: 80px;
          height: 80px;
          background: radial-gradient(circle, rgba(66, 133, 244, 0.4), transparent 70%);
          border-radius: 50%;
          filter: blur(15px);
          opacity: 0.6;
        }

        .subject-icon i {
          position: relative;
          z-index: 1;
          filter: drop-shadow(0 4px 15px rgba(0, 0, 0, 0.5)) 
                  drop-shadow(0 0 20px rgba(66, 133, 244, 0.4));
          transition: all 0.3s ease;
        }

        .subject-card-modern:hover .subject-icon i {
          transform: scale(1.15);
          filter: drop-shadow(0 6px 20px rgba(0, 0, 0, 0.6)) 
                  drop-shadow(0 0 30px rgba(66, 133, 244, 0.5));
        }

        /* Enhanced Title */
        .subject-content h5 {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-shadow: 0 3px 12px rgba(0, 0, 0, 0.6);
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        /* Enhanced Description */
        .subject-content p {
          color: rgba(255, 255, 255, 0.85) !important;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
          font-weight: 400;
          letter-spacing: 0.3px;
          line-height: 1.6;
        }

        /* Enhanced Stats */
        .subject-stats > div {
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.3) 0%, 
            rgba(255, 255, 255, 0.05) 100%
          );
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          padding: 1rem 1.25rem;
          min-width: 80px;
          margin: 0 0.25rem;
        }

        .subject-stats > div:hover {
          transform: scale(1.02);
          background: linear-gradient(135deg, 
            rgba(66, 133, 244, 0.08) 0%,
            rgba(0, 0, 0, 0.5) 100%
          );
          border-color: rgba(66, 133, 244, 0.2);
        }

        .subject-stats .h6 {
          font-weight: 700;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          margin-bottom: 0.5rem !important;
          font-size: 1.1rem;
        }

        .subject-stats .text-primary {
          color: #4285F4 !important;
        }

        .subject-stats .text-success {
          color: #10b981 !important;
        }

        .subject-stats .text-warning {
          color: #f59e0b !important;
        }

        .subject-stats small {
          color: rgba(255, 255, 255, 0.9) !important;
          font-weight: 500;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
          letter-spacing: 0.3px;
          text-transform: uppercase;
          font-size: 0.7rem;
          margin-top: 0.25rem;
          display: block;
        }

        /* Enhanced Button Styling - Matching WhyCourse */
        .subject-actions .btn {
          border-radius: 50px !important;
          padding: 18px 36px !important;
          font-weight: 700 !important;
          font-size: 15px !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          min-width: 200px;
          color: #ffffff !important;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.8) 0%, 
            rgba(15, 15, 15, 0.7) 50%,
            rgba(0, 0, 0, 0.8) 100%
          ) !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.15),
                      inset 0 -1px 0 rgba(0, 0, 0, 0.2) !important;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          will-change: transform, box-shadow;
        }

        .subject-actions .btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }

        .subject-actions .btn:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(0, 0, 0, 0.9) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          transform: translateY(-6px) scale(1.08);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 
                      0 0 50px rgba(255, 255, 255, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.25),
                      inset 0 -2px 0 rgba(0, 0, 0, 0.3) !important;
          border: 2px solid rgba(255, 255, 255, 0.5) !important;
        }

        .subject-actions .btn:hover .subject-button-shine {
          left: 100% !important;
          opacity: 1;
        }

        /* Button shine effect */
        .subject-button-shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.2) 45%,
            rgba(255, 255, 255, 0.4) 50%, 
            rgba(255, 255, 255, 0.2) 55%,
            transparent 100%
          );
          transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          border-radius: 50px;
          transform: skewX(-20deg);
          opacity: 0;
        }

        /* Shine Effect */
        .subject-shine-effect {
          position: absolute;
          top: 0;
          left: -120%;
          width: 120%;
          height: 100%;
          background: linear-gradient(
            110deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.1) 45%,
            rgba(255, 255, 255, 0.3) 50%, 
            rgba(255, 255, 255, 0.1) 55%,
            transparent 100%
          );
          transition: left 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          border-radius: 28px;
          transform: skewX(-15deg);
          opacity: 0;
        }

        /* Performance optimizations */
        .subject-card-modern,
        .subject-actions .btn {
          backface-visibility: hidden;
          perspective: 1000px;
        }

        /* Floating animation for cards */
        @keyframes cardFloat {
          0%, 100% { 
            transform: translateY(0px);
          }
          50% { 
            transform: translateY(-5px);
          }
        }

        /* Arrow styling */
        .subject-arrow {
          margin-left: 8px;
          font-size: 16px;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .subject-content {
            padding: 2rem 1.5rem !important;
          }
          
          .subject-actions .btn {
            padding: 14px 28px !important;
            font-size: 13px !important;
          }
          
          .subject-content h5 {
            font-size: 1.4rem;
          }
        }

        @media (max-width: 480px) {
          .subject-content {
            padding: 1.5rem 1rem !important;
          }
          
          .subject-actions .btn {
            padding: 12px 24px !important;
            font-size: 12px !important;
          }

          .subject-stats > div {
            padding: 0.75rem 1rem;
            margin: 0 0.15rem;
            min-width: 70px;
          }

          .subject-stats .h6 {
            font-size: 1rem;
          }

          .subject-stats small {
            font-size: 0.65rem;
          }
        }

        /* Hero section enhancements */
        .exam-hero {
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }

        .hero-content h2 {
          color: white !important;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .hero-content p {
          color: rgba(255, 255, 255, 0.8) !important;
        }

      `}</style>
      
      <div className="container-fluid px-4">
        {/* Hero Header */}
        <div className="exam-hero mb-5">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="hero-content">
                <h2 className="text-white mb-3 display-6">
                  {examData.title}
                </h2>
                <p className="text-light fs-5 mb-4 opacity-75">
                  {examData.description || 'Comprehensive study materials and practice tests for your exam preparation.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Header Section */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="text-white mb-2">Exam Subjects</h4>
                <p className="text-light mb-0">{subjects.length} subjects available • Choose a subject to start learning</p>
                {isSubscriptionExpired && (
                  <p className="text-warning mt-2 mb-0">
                    {examPurchaseStatus.message || RENEWAL_MESSAGE}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Subjects Grid */}
        <div className="row g-4">
          {subjects.map((subject, index) => (
            <div key={subject._id || index} className="col-lg-4 col-md-6">
              <div className="subject-card-modern position-relative h-100">
                {/* Shine Effect */}
                <div className="subject-shine-effect"></div>
                
                <div className="subject-content p-4 h-100 d-flex flex-column">
                  <div className="subject-icon mb-3">
                    <FontAwesomeIcon 
                      icon={faGraduationCap} 
                      className="text-primary" 
                      size="2x" 
                    />
                  </div>
                  
                  <h5 className="text-white mb-3">
                    {subject.title}
                  </h5>
                  
                  {subject.description && (
                    <p className="text-light mb-4 flex-grow-1">
                      {subject.description}
                    </p>
                  )}
                  
                   <div className="subject-stats d-flex justify-content-between mb-4" style={{ gap: '0.5rem', padding: '0 0.5rem' }}>
                    <div className="text-center">
                      <div className="text-primary h6 mb-1">
                        {subject.counts?.videos || 0}
                      </div>
                      <small className="text-light">Videos</small>
                    </div>
                    <div className="text-center">
                      <div className="text-success h6 mb-1">
                        {subject.counts?.mockTests || 0}
                      </div>
                      <small className="text-light">Tests</small>
                    </div>
                    <div className="text-center">
                      <div className="text-warning h6 mb-1">
                        {subject.counts?.materails ?? subject.counts?.materials ?? 0}
                      </div>
                      <small className="text-light">Materials</small>
                    </div>
                  </div>
                  
                  <div className="subject-actions mt-auto">
                    {isSubjectAccessible(subject) ? (
                      <button 
                        className="btn btn-light w-100 py-2"
                        onClick={() => navigate(`/exam/${examCode}/subject/${subject._id}`)}
                      >
                        <div className="subject-button-shine"></div>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        Watch For Free
                        <span className="subject-arrow">→</span>
                      </button>
                    ) : (
                      <button 
                        className="btn btn-secondary w-100 py-2"
                        onClick={() => handlePurchaseClick(examData)}
                        disabled={processingPayment}
                      >
                        <FontAwesomeIcon icon={faLock} className="me-2" />
                        {processingPayment
                          ? 'Processing Payment...'
                          : isSubscriptionExpired
                            ? 'Renew Access'
                            : 'Purchase Exam to Access'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExamPage; 